import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Loader2 } from 'lucide-react';

interface Category {
  id: string;
  name: string;
}

interface Unit {
  id: string;
  name: string;
  symbol: string;
  is_default: boolean;
}

interface AddCustomItemFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CustomItemData) => Promise<void>;
  isSubmitting?: boolean;
}

export interface CustomItemData {
  description: string;
  quantity: number;
  unit: string;
  categoryId: string;
  baseCost: number;
  markupPercentage: number;
}

const AddCustomItemForm: React.FC<AddCustomItemFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false
}) => {
  const { profile } = useAuth();
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [unit, setUnit] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [baseCost, setBaseCost] = useState<number | ''>(0);
  const [markupPercentage, setMarkupPercentage] = useState<number | ''>(25);
  const [isMarkupManual, setIsMarkupManual] = useState(false);
  const isMarkupManualRef = useRef(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingUnits, setLoadingUnits] = useState(true);

  // Calculated values - handle empty values as 0 for calculations
  const effectiveMarkup = markupPercentage === '' ? 0 : markupPercentage;
  const effectiveQuantity = quantity === '' ? 0 : quantity;
  const effectiveBaseCost = baseCost === '' ? 0 : baseCost;
  const unitFinalPrice = effectiveBaseCost * (1 + effectiveMarkup / 100);
  const totalPrice = unitFinalPrice * effectiveQuantity;

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      const { data, error } = await supabase
        .from('supplier_categories')
        .select('id, name')
        .order('name');
      
      if (!error && data) {
        setCategories(data);
        // Set default category if available
        if (data.length > 0 && !categoryId) {
          setCategoryId(data[0].id);
        }
      }
      setLoadingCategories(false);
    };

    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Fetch units from cost center database
  useEffect(() => {
    const fetchUnits = async () => {
      if (!profile?.company_id) return;
      
      setLoadingUnits(true);
      const { data, error } = await supabase
        .rpc('get_all_units', { user_company_id: profile.company_id });
      
      if (!error && data) {
        // Sort: default first, then by name
        const sortedUnits = [...data].sort((a: Unit, b: Unit) => {
          if (a.is_default && !b.is_default) return -1;
          if (!a.is_default && b.is_default) return 1;
          return a.name.localeCompare(b.name);
        });
        setUnits(sortedUnits);
        // Set default unit if available and no unit selected
        const defaultUnit = sortedUnits.find((u: Unit) => u.is_default);
        if (defaultUnit && !unit) {
          setUnit(defaultUnit.symbol);
        } else if (sortedUnits.length > 0 && !unit) {
          setUnit(sortedUnits[0].symbol);
        }
      }
      setLoadingUnits(false);
    };

    if (isOpen && profile?.company_id) {
      fetchUnits();
    }
  }, [isOpen, profile?.company_id]);

  // Fetch category markup when category changes
  useEffect(() => {
    const fetchCategoryMarkup = async () => {
      if (!categoryId || !profile?.company_id) return;
      if (isMarkupManualRef.current) return;
      
      try {
        const { data, error } = await supabase.rpc('get_category_markup', {
          p_category_id: categoryId,
          p_amount: effectiveBaseCost,
          p_company_id: profile.company_id
        });
        
        if (!error && data !== null) {
          setMarkupPercentage(data);
        }
      } catch (err) {
        console.error('Error fetching category markup:', err);
      }
    };

    fetchCategoryMarkup();
  }, [categoryId, baseCost, profile?.company_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!description.trim() || effectiveBaseCost <= 0 || !categoryId || quantity === '' || quantity <= 0) {
      return;
    }

    await onSubmit({
      description: description.trim(),
      quantity: effectiveQuantity,
      unit,
      categoryId,
      baseCost: effectiveBaseCost,
      markupPercentage: effectiveMarkup
    });

    // Reset form
    setDescription('');
    setQuantity(1);
    setUnit('');
    setBaseCost(0);
    setMarkupPercentage(25);
  };

  const handleClose = () => {
    // Reset form on close
    setDescription('');
    setQuantity(1);
    setUnit('');
    setBaseCost(0);
    setMarkupPercentage(25);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Custom Item
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter item description"
              required
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select value={categoryId} onValueChange={(val) => { setIsMarkupManual(false); isMarkupManualRef.current = false; setCategoryId(val); }} disabled={loadingCategories}>
              <SelectTrigger>
                <SelectValue placeholder={loadingCategories ? 'Loading...' : 'Select category'} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quantity and Unit in a row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity *</Label>
              <Input
                id="quantity"
                type="number"
                step="0.01"
                min="0.01"
                value={quantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setQuantity('');
                  } else {
                    const num = parseFloat(val);
                    if (!isNaN(num) && num >= 0) {
                      setQuantity(num);
                    }
                  }
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit">Unit *</Label>
              <Select value={unit} onValueChange={setUnit} disabled={loadingUnits}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingUnits ? 'Loading...' : 'Select unit'} />
                </SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.symbol}>
                      {u.name} ({u.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Base Cost */}
          <div className="space-y-2">
            <Label htmlFor="baseCost">Base Cost (Your Cost) *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R</span>
              <Input
                id="baseCost"
                type="number"
                step="0.01"
                min="0"
                value={baseCost}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') setBaseCost('');
                  else { const num = parseFloat(val); if (!isNaN(num)) setBaseCost(num); }
                }}
                onBlur={() => { if (baseCost === '') setBaseCost(0); }}
                className="pl-8"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* Markup */}
          <div className="space-y-2">
            <Label htmlFor="markup">
              Markup %{' '}
              {isMarkupManual ? (
                <span className="text-xs font-normal text-muted-foreground">
                  (Manual)
                </span>
              ) : (
                <span className="text-xs font-normal text-muted-foreground">
                  (Auto from category tier)
                </span>
              )}
            </Label>
            <div className="relative">
              <Input
                id="markup"
                type="number"
                step="0.1"
                min="0"
                max="500"
                value={markupPercentage}
                onChange={(e) => {
                  const val = e.target.value;
                  setIsMarkupManual(true);
                  isMarkupManualRef.current = true;
                  if (val === '') {
                    setMarkupPercentage('');
                  } else {
                    const num = parseFloat(val);
                    if (!isNaN(num)) {
                      setMarkupPercentage(num);
                    }
                  }
                }}
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
            </div>
          </div>

          {/* Calculated Values */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Unit Final Price:</span>
              <span className="font-medium">{formatCurrency(unitFinalPrice)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-2">
              <span>Total:</span>
              <span className="text-primary">{formatCurrency(totalPrice)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {effectiveQuantity} × {formatCurrency(unitFinalPrice)} per {unit}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!description.trim() || effectiveBaseCost <= 0 || !categoryId || quantity === '' || quantity <= 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Item
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomItemForm;
